import { AxiosResponse } from 'axios';

import { DETAILS_DATA_TYPES } from 'store/details/utils/constants';
import requestRecordingDetailsData from 'store/details/utils/requestRecordingDetailsData.ts';
import {
    GetRecordingsResponse,
    RBProgram,
} from 'store/railsBuilder/reducer.types';
import { RecordingItem } from 'store/recordings/reducer.types';
import { AsyncActionMetaOptions } from 'store/types.ts';

import isCatchupDetails from 'services/data-manager/catchup/isCatchupDetails';

import { isFutureProgram, isLive } from 'screens/Epg/utils/program';

import isBtvProgrammesResponse from './isBtvProgrammesResponse';
import isRecordingsResponse from './isRecordingsResponse';
import requestAssetDetailsData from './requestAssetDetailsData';

type Params = {
    id: string | undefined;
    channelId?: string | undefined;
} & Pick<
    AsyncActionMetaOptions,
    'abortController' | 'autoRetry' | 'retryCount' | 'retryDuration'
>;

export type LoadBTVProgramDataReturnType =
    | RBProgram
    | RecordingItem
    | undefined;

async function loadBTVProgramData({
    channelId,
    id,
    abortController,
    autoRetry,
    retryCount,
    retryDuration,
}: Params): Promise<LoadBTVProgramDataReturnType> {
    const programDetailsResponse = await requestAssetDetailsData({
        type: DETAILS_DATA_TYPES.PROGRAM,
        id,
        channelId,
        abortController,
        autoRetry,
        retryCount,
        retryDuration,
    });

    let details: RBProgram | RecordingItem | undefined;
    let btvProgramDetails: RBProgram | undefined;

    if (
        isBtvProgrammesResponse(programDetailsResponse) &&
        programDetailsResponse.data.programmes[0]
    ) {
        [btvProgramDetails] = programDetailsResponse.data.programmes;
    }

    if (btvProgramDetails && isLive(btvProgramDetails)) {
        details = btvProgramDetails;
    } else if (btvProgramDetails && isCatchupDetails(btvProgramDetails)) {
        details = btvProgramDetails;
    } else if (btvProgramDetails && isFutureProgram(btvProgramDetails)) {
        details = btvProgramDetails;
    } else {
        const recordingDetailsResponse:
            | AxiosResponse<GetRecordingsResponse>
            | undefined = await requestRecordingDetailsData({
            id,
            autoRetry,
            abortController,
            retryCount,
            retryDuration,
        });

        if (
            isRecordingsResponse(recordingDetailsResponse) &&
            recordingDetailsResponse.data.recordingSet.length
        ) {
            [details] = recordingDetailsResponse.data.recordingSet;
        } else {
            details = btvProgramDetails;
        }
    }

    if (!details) {
        throw new Error('Program was not found in programmes or recordings');
    }

    return details;
}

export default loadBTVProgramData;
